import $$ from '@/js/core/dom-query.js';
import '@/js/settings';
import renderer from '@/js/renderer';
import { ICON_PREFIX, getIconSvg, loadAllIcons, iconNames } from '@/js/icons-svg.js';

import './summernote-bs5.scss';

const editor = renderer.create('<div class="note-editor note-frame card"></div>');
const toolbar = renderer.create('<div class="note-toolbar card-header" role="toolbar"></div>');
const editingArea = renderer.create('<div class="note-editing-area"></div>');
const codable = renderer.create('<textarea class="note-codable" aria-multiline="true"></textarea>');
const editable = renderer.create('<div class="note-editable card-block" contentEditable="true" role="textbox" aria-multiline="true"/>');
const statusbar = renderer.create([
  '<output class="note-status-output" role="status" aria-live="polite"></output>',
  '<div class="note-statusbar card-footer border-top-0 shadow-sm" role="status">',
    '<div class="note-resizebar" aria-label="Resize">',
      '<div class="note-icon-bar"></div>',
      '<div class="note-icon-bar"></div>',
      '<div class="note-icon-bar"></div>',
    '</div>',
  '</div>',
].join(''));

const airEditor = renderer.create('<div class="note-editor note-airframe"></div>');
const airEditable = renderer.create([
  '<div class="note-editable" contentEditable="true" role="textbox" aria-multiline="true"></div>',
  '<output class="note-status-output" role="status" aria-live="polite"></output>',
].join(''));

const buttonGroup = renderer.create('<div class="note-btn-group btn-group" role="group"></div>');

const dropdown = renderer.create('<div class="note-dropdown-menu dropdown-menu" role="list">', function($node, options) {
  if (options && options.items !== undefined) {
    const markup = Array.isArray(options.items) ? options.items.map(function(item) {
      const value = (typeof item === 'string') ? item : (item.value || '');
      const content = options.template ? options.template(item) : item;
      const option = (typeof item === 'object') ? item.option : undefined;

      const dataValue = 'data-value="' + value + '"';
      const dataOption = (option !== undefined) ? ' data-option="' + option + '"' : '';
      return '<a class="dropdown-item" href="#" ' + (dataValue + dataOption) + ' role="listitem" aria-label="' + value + '">' + content + '</a>';
    }).join('') : options.items;

    $node.html(markup);
  }

  if (options && options.title) {
    $node.attr({ 'aria-label': options.title });
  }

  if (options && options.codeviewKeepButton) {
    $node.addClass('note-codeview-keep');
  }
});

const dropdownButtonContents = function(contents) {
  return contents;
};

const dropdownCheck = renderer.create('<div class="note-dropdown-menu dropdown-menu note-check" role="list">', function($node, options) {
  if (options && options.items !== undefined) {
    const markup = Array.isArray(options.items) ? options.items.map(function(item) {
      const value = (typeof item === 'string') ? item : (item.value || '');
      const content = options.template ? options.template(item) : item;
      return '<a class="dropdown-item" href="#" data-value="' + value + '" role="listitem" aria-label="' + item + '">' + icon(options.checkClassName) + ' ' + content + '</a>';
    }).join('') : options.items;
    $node.html(markup);
  }

  if (options && options.title) {
    $node.attr({ 'aria-label': options.title });
  }

  if (options && options.codeviewKeepButton) {
    $node.addClass('note-codeview-keep');
  }
});

const dialog = renderer.create('<div class="modal note-modal" aria-hidden="false" tabindex="-1" role="dialog"></div>', function($node, options) {
  if (options.fade) {
    $node.addClass('fade');
  }
  $node.attr({
    'aria-label': options.title,
  });
  $node.html([
    '<div class="modal-dialog">',
      '<div class="modal-content">',
        (options.title ? '<div class="modal-header">' +
          '<h4 class="modal-title">' + options.title + '</h4>' +
          '<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" aria-hidden="true"></button>' +
        '</div>' : ''),
        '<div class="modal-body">' + options.body + '</div>',
        (options.footer ? '<div class="modal-footer">' + options.footer + '</div>' : ''),
      '</div>',
    '</div>',
  ].join(''));
});

const popover = renderer.create([
  '<div class="note-popover popover bs-popover-auto show">',
    '<div class="popover-body note-popover-content note-children-container"></div>',
  '</div>',
].join(''), function($node, options) {
  const direction = typeof options.direction !== 'undefined' ? options.direction : 'bottom';

  $node.attr('data-popper-placement', direction);
});

const checkbox = renderer.create('<div class="form-check"></div>', function($node, options) {
  $node.html([
    '<label class="form-check-label"' + (options.id ? ' for="note-' + options.id + '"' : '') + '>',
      '<input type="checkbox" class="form-check-input"' + (options.id ? ' id="note-' + options.id + '"' : ''),
        (options.checked ? ' checked' : ''),
        ' aria-label="' + (options.text ? options.text : '') + '"',
        ' aria-checked="' + (options.checked ? 'true' : 'false') + '"/>',
      ' ' + (options.text ? options.text : '') +
    '</label>',
  ].join(''));
});

const icon = function(iconClassName, tagName) {
  if (!iconClassName) {
    return '';
  }
  if (iconClassName.charAt(0) === '<') {
    return iconClassName;
  }
  const tokens = iconClassName.split(/\s+/).filter(Boolean);
  const iconToken = tokens.find((token) => token.startsWith(ICON_PREFIX));
  if (!iconToken) {
    tagName = tagName || 'i';
    return '<' + tagName + ' class="' + iconClassName + '"></' + tagName + '>';
  }
  const name = iconToken.slice(ICON_PREFIX.length);
  const extra = tokens.filter((token) => token !== iconToken).join(' ').trim();
  const cls = 'note-icon ' + ICON_PREFIX + name + (extra ? ' ' + extra : '');
  const svg = getIconSvg(name) || '';
  return '<i class="' + cls + '" aria-hidden="true">' + svg + '</i>';
};

const paintIcon = function(name, lookup) {
  const sourceLookup = lookup || getIconSvg;
  const svg = sourceLookup(name);
  if (!svg) {
    return;
  }
  const nodes = document.querySelectorAll('i.' + ICON_PREFIX + name);
  nodes.forEach((el) => {
    if (!el.querySelector(':scope > svg')) {
      el.innerHTML = svg;
    }
  });
};

const paintAllIcons = function() {
  iconNames().forEach((name) => paintIcon(name));
};

let paintedPromise = null;
const ensureIconsLoaded = function() {
  const promise = loadAllIcons();
  if (promise === paintedPromise) {
    return;
  }
  paintedPromise = promise;
  promise.then(() => paintAllIcons()).catch(() => {
    paintedPromise = null;
  });
};

const initializeTooltip = function($node, options, editorOptions) {
  if (!options || !options.tooltip) {
    return;
  }

  const tooltipContainer = options.container || editorOptions.container || undefined;

  $node.attr({
    title: options.tooltip,
    'aria-label': options.tooltip,
  }).tooltip({
    ...(tooltipContainer ? { container: tooltipContainer } : {}),
    trigger: 'hover',
    placement: 'bottom',
  }).on('click', (e) => {
    $$(e.currentTarget).tooltip('hide');
  });
};

const ui = function(editorOptions) {
  ensureIconsLoaded();

  return {
    editor: editor,
    toolbar: toolbar,
    editingArea: editingArea,
    codable: codable,
    editable: editable,
    statusbar: statusbar,
    airEditor: airEditor,
    airEditable: airEditable,
    buttonGroup: buttonGroup,
    dropdown: dropdown,
    dropdownButtonContents: dropdownButtonContents,
    dropdownCheck: dropdownCheck,
    dialog: dialog,
    popover: popover,
    icon: icon,
    checkbox: checkbox,
    options: editorOptions,

    palette: function($node, options) {
      return renderer.create('<div class="note-color-palette"></div>', function($node, options) {
        const contents = [];
        for (let row = 0, rowSize = options.colors.length; row < rowSize; row++) {
          const eventName = options.eventName;
          const colors = options.colors[row];
          const colorsName = options.colorsName[row];
          const buttons = [];
          for (let col = 0, colSize = colors.length; col < colSize; col++) {
            const color = colors[col];
            const colorName = colorsName[col];
            buttons.push([
              '<button type="button" class="note-color-btn"',
              'style="background-color:', color, '" ',
              'data-event="', eventName, '" ',
              'data-value="', color, '" ',
              'title="', colorName, '" ',
              'aria-label="', colorName, '" ',
              'data-toggle="button" tabindex="-1"></button>',
            ].join(''));
          }
          contents.push('<div class="note-color-row">' + buttons.join('') + '</div>');
        }
        $node.html(contents.join(''));

        if (options.tooltip) {
          const tooltipContainer = options.container || editorOptions.container || undefined;
          var tooltipOptions = {
            ...(tooltipContainer ? { container: tooltipContainer } : {}),
            trigger: 'hover',
            placement: 'bottom',
          };

          $node.tooltip({
            selector: '.note-color-btn',
            ...tooltipOptions,
          });
        }
      })($node, options);
    },

    button: function($node, options) {
      return renderer.create('<button type="button" class="note-btn btn btn-outline-secondary btn-sm" tabindex="-1">', function($node, options) {
        if (options && options.data && options.data.toggle === 'dropdown') {
          $node.removeAttr('data-toggle');
          $node.attr({
            'data-note-toggle': 'dropdown',
            'aria-expanded': 'false',
            'aria-haspopup': 'true',
          });
        }

        initializeTooltip($node, options, editorOptions);

        if (options && options.codeviewButton) {
          $node.addClass('note-codeview-keep');
        }
      })($node, options);
    },

    toggleBtn: function($btn, isEnable) {
      $btn.toggleClass('disabled', !isEnable);
      $btn.prop('disabled', !isEnable);
      $btn.attr('disabled', !isEnable);
    },

    toggleBtnActive: function($btn, isActive) {
      $btn.toggleClass('active', isActive);
    },

    onDialogShown: function($dialog, handler) {
      $dialog.one('shown.bs.modal', handler);
    },

    onDialogHidden: function($dialog, handler) {
      $dialog.one('hidden.bs.modal', handler);
    },

    showDialog: function($dialog) {
      $dialog.modal('show');
    },

    hideDialog: function($dialog) {
      $dialog.modal('hide');
    },

    createLayout: function($note) {
      const $editor = (editorOptions.airMode ? airEditor([
        editingArea([
          codable(),
          airEditable(),
        ]),
      ]) : (editorOptions.toolbarPosition === 'bottom'
        ? editor([
          editingArea([
            codable(),
            editable(),
          ]),
          toolbar(),
          statusbar(),
        ])
        : editor([
          toolbar(),
          editingArea([
            codable(),
            editable(),
          ]),
          statusbar(),
        ])
      )).render();

      if (!editorOptions.airMode) {
        $editor.addClass(`note-editor-toolbar-${editorOptions.toolbarPosition}`);
      }

      $editor.insertAfter($note);

      return {
        note: $note,
        editor: $editor,
        toolbar: $editor.find('.note-toolbar'),
        editingArea: $editor.find('.note-editing-area'),
        editable: $editor.find('.note-editable'),
        codable: $editor.find('.note-codable'),
        statusbar: $editor.find('.note-statusbar'),
      };
    },

    removeLayout: function($note, layoutInfo) {
      $note.html(layoutInfo.editable.html());
      layoutInfo.editor.remove();
      $note.show();
    },
  };
};

$$.summernote = Object.assign($$.summernote, {
  ui_template: ui,
  interface: 'bs5',
  __paintIcon__: paintIcon,
});

$$.summernote.options.styleTags = [
  'p',
  { title: 'Blockquote', tag: 'blockquote', className: 'blockquote', value: 'blockquote' },
  'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
];

export default $$;